import { useEffect, useRef, useState } from "react";
import {
  MagnifyingGlassIcon, ChevronDownIcon, ArrowRightIcon, ExclamationTriangleIcon, ServerStackIcon,
} from "@heroicons/react/24/outline";
import { STATUS_LABELS, STATUS_DOT_BG, STATUS_ORDER, type MigrationStatusKey, type TargetSummary } from "./utils/constants";

interface Props {
  search: string;
  onSearchChange: (s: string) => void;

  visibleStatuses: Set<MigrationStatusKey>;
  onStatusToggle: (s: MigrationStatusKey) => void;

  inferredFilter: boolean;
  onInferredToggle: () => void;
  inferredCount: number;

  statusCounts: Record<MigrationStatusKey, number>;

  availableTargets: TargetSummary[];
  selectedTargetIds: Set<string>;
  onTargetsChange: (next: Set<string>) => void;

  bulkApplyEnabled: boolean;
  bulkApplyLabel: string;
  bulkSkippedCount?: number;
  onBulkApplyClick: () => void;
}

export default function MigrationsToolbar(props: Props) {
  const [targetMenuOpen, setTargetMenuOpen] = useState(false);
  const [targetSearch, setTargetSearch] = useState("");
  const pickerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedCount = props.selectedTargetIds.size;
  const totalCount = props.availableTargets.length;

  useEffect(() => {
    if (!targetMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setTargetMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTargetMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    // focus the filter as soon as the menu opens
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [targetMenuOpen]);

  // Registry first, then tenants alphabetically — stable, predictable order.
  const sortedTargets = [...props.availableTargets].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "registry" ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  const q = targetSearch.trim().toLowerCase();
  const filteredTargets = q
    ? sortedTargets.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.databaseName.toLowerCase().includes(q),
      )
    : sortedTargets;
  const filteredIds = filteredTargets.map((t) => t.id);
  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every((id) => props.selectedTargetIds.has(id));

  return (
    <div className="sticky top-0 z-10 rounded-t-xl bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-2.5 flex-wrap">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
        <input
          type="search"
          value={props.search}
          onChange={(e) => props.onSearchChange(e.target.value)}
          placeholder="Search by filename, folder, or SQL…"
          className="bg-slate-50 border border-slate-200 rounded-lg pl-7 pr-3 py-1.5 text-[11px] text-slate-700 placeholder:text-slate-400 w-[260px] focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
        />
      </div>

      {STATUS_ORDER.map((status) => {
        const on = props.visibleStatuses.has(status);
        const count = props.statusCounts[status] ?? 0;
        return (
          <button
            key={status}
            type="button"
            onClick={() => props.onStatusToggle(status)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold ${
              on
                ? "bg-blue-50 border-blue-300 text-blue-700"
                : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_BG[status]}`} aria-hidden="true"></span>
            {STATUS_LABELS[status]}
            <span className="bg-slate-900/10 px-1.5 rounded-md text-[9px]">{count}</span>
          </button>
        );
      })}

      <button
        type="button"
        onClick={props.onInferredToggle}
        title="Legacy migrations missing a -- @scope: header are hidden by default (their tenant/registry scope is unconfirmed, so Apply can't touch them). Toggle to review this backlog."
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-semibold ${
          props.inferredFilter
            ? "bg-amber-100 border-amber-300 text-amber-800"
            : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100"
        }`}
      >
        <ExclamationTriangleIcon className="w-2.5 h-2.5" />
        Inferred scope
        <span className="bg-slate-900/10 px-1.5 rounded-md text-[9px]">{props.inferredCount}</span>
      </button>

      <div ref={pickerRef} className="relative">
        <button
          type="button"
          onClick={() => setTargetMenuOpen((v) => !v)}
          className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white border rounded-lg text-[11px] text-slate-700 hover:bg-slate-50 ${
            targetMenuOpen ? "border-blue-400 ring-2 ring-blue-200" : "border-slate-300"
          }`}
        >
          <ServerStackIcon className="w-3.5 h-3.5 text-slate-400" />
          Targets: <strong className="text-slate-900">{selectedCount} of {totalCount}</strong>
          <ChevronDownIcon className="w-2.5 h-2.5 text-slate-400" />
        </button>
        {targetMenuOpen && (
          <div className="absolute top-full mt-1.5 right-0 w-[420px] bg-white border border-slate-200 rounded-xl shadow-2xl z-20 flex flex-col overflow-hidden">
            {/* Search + bulk actions */}
            <div className="p-3 border-b border-slate-100 bg-slate-50/60">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="search"
                  value={targetSearch}
                  onChange={(e) => setTargetSearch(e.target.value)}
                  placeholder="Filter companies…"
                  className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-[13px] text-slate-700 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-between mt-2.5 px-0.5">
                <span className="text-[11px] text-slate-500">
                  <strong className="text-slate-700">{selectedCount}</strong> selected
                  {q && (
                    <span className="text-slate-400"> · {filteredTargets.length} shown</span>
                  )}
                </span>
                <span className="flex gap-3 text-[12px] font-semibold">
                  <button
                    type="button"
                    onClick={() => {
                      const next = new Set(props.selectedTargetIds);
                      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
                      else filteredIds.forEach((id) => next.add(id));
                      props.onTargetsChange(next);
                    }}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    {allFilteredSelected ? "Deselect all" : q ? "Select shown" : "Select all"}
                  </button>
                  <button
                    type="button"
                    onClick={() => props.onTargetsChange(new Set())}
                    className="text-slate-500 hover:text-slate-700 disabled:opacity-40"
                    disabled={selectedCount === 0}
                  >
                    Clear
                  </button>
                </span>
              </div>
            </div>

            {/* Target list */}
            <div className="max-h-[360px] overflow-y-auto p-2">
              {filteredTargets.length === 0 ? (
                <div className="px-3 py-8 text-center text-[12px] text-slate-400">
                  No companies match “{targetSearch}”.
                </div>
              ) : (
                filteredTargets.map((t) => {
                  const checked = props.selectedTargetIds.has(t.id);
                  return (
                    <label
                      key={t.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer ${
                        checked ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(props.selectedTargetIds);
                          if (checked) next.delete(t.id);
                          else next.add(t.id);
                          props.onTargetsChange(next);
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 focus:ring-2 shrink-0"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-medium text-slate-800 truncate">
                          {t.name}
                        </span>
                        <span className="block text-[10px] text-slate-400 font-mono truncate">
                          {t.databaseName}
                        </span>
                      </span>
                      <span
                        className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md border shrink-0 ${
                          t.kind === "tenant"
                            ? "bg-sky-100 text-sky-700 border-sky-200"
                            : "bg-purple-100 text-purple-700 border-purple-200"
                        }`}
                      >
                        {t.kind}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={props.onBulkApplyClick}
        disabled={!props.bulkApplyEnabled}
        title={
          props.bulkSkippedCount && props.bulkSkippedCount > 0
            ? `${props.bulkSkippedCount} inferred-scope migration${props.bulkSkippedCount === 1 ? "" : "s"} excluded — add -- @scope: headers to include them.`
            : undefined
        }
        className={`ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[11px] font-bold ${
          props.bulkApplyEnabled
            ? "bg-blue-600 text-white hover:bg-blue-700"
            : "bg-slate-200 text-slate-400 cursor-not-allowed"
        }`}
      >
        <ArrowRightIcon className="w-3 h-3" />
        {props.bulkApplyLabel}
      </button>
    </div>
  );
}
